/**
 * WorkspaceSwitcher — Selector de workspace activo.
 * Dropdown con lista de workspaces donde el user es miembro.
 * Cambia activeWorkspace y resetea activeBrand al seleccionar uno nuevo.
 */
import React, { useState } from 'react';
import { useWorkspacesV2 } from '@/contexts/WorkspacesV2Context';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { Building2, Check, ChevronsUpDown, Plus, Crown, Shield, User } from 'lucide-react';
import { toast } from 'sonner';

const ROLE_ICONS = {
  owner: Crown,
  admin: Shield,
  member: User,
};

const ROLE_LABELS = {
  owner: 'Propietario',
  admin: 'Admin',
  member: 'Miembro',
};

export default function WorkspaceSwitcher({ onCreateWorkspace }) {
  const {
    workspaces,
    activeWorkspace,
    setActiveWorkspace,
    loading,
  } = useWorkspacesV2();

  const [open, setOpen] = useState(false);

  if (loading) {
    return <Skeleton className="h-10 w-[220px]" />;
  }

  if (workspaces.length === 0) {
    return (
      <Button
        variant="outline"
        size="sm"
        className="w-[220px] justify-start gap-2"
        onClick={onCreateWorkspace}
      >
        <Plus className="h-4 w-4" />
        Crear workspace
      </Button>
    );
  }

  const handleSelect = (ws) => {
    if (ws.id === activeWorkspace?.id) {
      setOpen(false);
      return;
    }
    setActiveWorkspace(ws);
    setOpen(false);
    toast.success(`Cambiado a "${ws.name}"`);
  };

  const ActiveRoleIcon = ROLE_ICONS[activeWorkspace?.my_role] || User;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          role="combobox"
          aria-expanded={open}
          className="w-[260px] justify-between gap-2"
        >
          <div className="flex items-center gap-2 min-w-0">
            <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate text-left">
              {activeWorkspace?.name || 'Seleccionar workspace'}
            </span>
          </div>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent className="w-[260px]" align="start">
        <DropdownMenuLabel>Mis workspaces</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {workspaces.map((ws) => {
          const RoleIcon = ROLE_ICONS[ws.my_role] || User;
          const isActive = ws.id === activeWorkspace?.id;
          return (
            <DropdownMenuItem
              key={ws.id}
              onSelect={() => handleSelect(ws)}
              className="flex items-center gap-2 cursor-pointer"
            >
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <div className="flex-1 min-w-0">
                <div className="truncate text-sm font-medium">{ws.name}</div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <RoleIcon className="h-3 w-3" />
                  <span>{ROLE_LABELS[ws.my_role] || ws.my_role}</span>
                  <span>·</span>
                  <span className="capitalize">{ws.plan_tier}</span>
                </div>
              </div>
              {isActive && <Check className="h-4 w-4 text-primary" />}
            </DropdownMenuItem>
          );
        })}

        {onCreateWorkspace && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => {
                setOpen(false);
                onCreateWorkspace();
              }}
              className="cursor-pointer"
            >
              <Plus className="h-4 w-4 mr-2" />
              <span>Crear nuevo workspace</span>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
